import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { create } from 'zustand';

import queryKeys from '@/configs/react-query-keys';
import taxonomyUtils from '@/features/Taxonomy/utils/taxonomy';
import service from '@/services/index';
import type { TOPIC_2_TYPE } from '@/types/taxonomyConst';
import { arrayToMap } from '@/utils/util';

type State = {
  topicTreeMap: Record<string, TopicTreeT>;
  fullTopicMap: Record<string, Topic2WithoutCount<TOPIC_2_TYPE>>;
};

type Actions = {
  fetchTaxonomyTopics: (taxonomyAlias: string) => Promise<TopicRes>;
  getTaxonomyRootTopics: (
    topicTreeMap: Record<string, TopicTreeT>,
    taxonomyAlias: string,
  ) => string[];
  toggleCollapseTopic: (taxonomyAlias: string, topicAlias: string) => void;
  setCollapseAll: (taxonomyAlias: string, status: boolean) => void;
  fetchFullTaxonomy: (workspace_id: string) => Promise<void>;
  initTaxonomyStore: () => void;
  setState: StoreSetter<State>;
};

const initialState: State = {
  topicTreeMap: {},
  fullTopicMap: {},
};

const useTaxonomyStore = create<State & Actions>((set, get) => ({
  ...initialState,
  getTaxonomyRootTopics: (topicTreeMap, taxonomyAlias) => {
    const root = topicTreeMap?.[taxonomyAlias]?.root;
    return root || [];
  },
  setCollapseAll(taxonomyAlias, status) {
    set((prev) => {
      const taxonomy = prev.topicTreeMap[taxonomyAlias];
      if (!taxonomy) throw new Error('Invalid taxonomyAlias');
      const newTopicMap = { ...taxonomy.topicMap };
      for (const [alias, topic] of Object.entries(newTopicMap)) {
        newTopicMap[alias] = {
          ...topic,
          collapsed: status,
        };
      }
      return {
        ...prev,
        topicTreeMap: {
          ...prev.topicTreeMap,
          [taxonomyAlias]: {
            taxonomy: taxonomy.taxonomy,
            root: taxonomy.root,
            topicMap: newTopicMap,
          },
        },
      };
    });
  },
  async fetchFullTaxonomy(workspace_id) {
    const res = await service.taxonomy.fetchTaxonomyList(workspace_id);
    const fullTopicMap: Record<string, Topic2WithoutCount<TOPIC_2_TYPE>> = {};
    for (const taxonomy of res) {
      const topics = await get().fetchTaxonomyTopics(taxonomy.alias);
      for (const topic of topics) {
        fullTopicMap[topic.alias] = topic;
      }
    }
    set({
      fullTopicMap,
    });
  },
  async fetchTaxonomyTopics(alias) {
    const originTaxonomy = await service.taxonomy.fetchTaxonomyItem(alias);
    const res = await service.taxonomy.fetchTaxonomyTopicList(alias);

    set((prev) => {
      const taxonomy = prev.topicTreeMap[alias];
      if (taxonomy) {
        const originTopicMap = {
          ...taxonomy.topicMap,
        };
        const newTopicMap: Topic2Map = {};
        for (const topic of res) {
          if (originTopicMap[topic.alias]) {
            newTopicMap[topic.alias] = {
              ...originTopicMap[topic.alias],
              ...topic,
              collapsed: originTopicMap[topic.alias].collapsed,
            };
          } else {
            newTopicMap[topic.alias] = {
              ...topic,
              taxonomyAlias: alias,
              collapsed: true,
              indent: topic.level,
            };
          }
        }

        return {
          ...prev,
          topicTreeMap: {
            ...prev.topicTreeMap,
            [alias]: {
              taxonomy: {
                alias: originTaxonomy.alias,
                title: originTaxonomy.title,
              },
              root: res.filter((t) => t.level === 1).map((t) => t.alias),
              topicMap: newTopicMap,
            },
          },
        };
      } else {
        const newTopicMap: Record<
          string,
          Topic2TreeItemT<TOPIC_2_TYPE>
        > = arrayToMap(
          res.map((t) => ({
            ...t,
            taxonomyAlias: alias,
            collapsed: true,
            indent: t.level,
          })),
          'alias',
        );
        return {
          ...prev,
          topicTreeMap: {
            ...prev.topicTreeMap,
            [alias]: {
              taxonomy: {
                alias: originTaxonomy.alias,
                title: originTaxonomy.title,
              },
              root: res.filter((t) => t.level === 1).map((t) => t.alias),
              topicMap: newTopicMap,
            },
          },
        };
      }
    });
    return res;
  },
  toggleCollapseTopic: (taxonomyAlias, topicAlias) => {
    const tree = taxonomyUtils.getTaxonomyTopicTree(
      get().topicTreeMap,
      taxonomyAlias,
    );
    const topic = taxonomyUtils.getTopicFromTopicTree(tree, topicAlias);
    const newTopic = {
      ...topic,
      collapsed: !topic.collapsed,
    };

    const newTree = {
      taxonomy: tree.taxonomy,
      root: tree.root,
      topicMap: {
        ...tree.topicMap,
        [topicAlias]: newTopic,
      },
    };

    set((prev) => {
      return {
        ...prev,
        topicTreeMap: {
          ...prev.topicTreeMap,
          [taxonomyAlias]: newTree,
        },
      };
    });
  },
  setState: set,
  initTaxonomyStore: () => set(initialState),
}));

const usePredictionStateUpdate = () => {
  const setTaxonomyStoreState = useTaxonomyStore((state) => state.setState);
  const queryClient = useQueryClient();

  const updateTaxonomyPredictionState = useCallback(
    (taxonomyAlias: string, payload: PredictionProgressPayload) => {
      const prevTaxonomyData: TaxonomyT | undefined = queryClient.getQueryData([
        ...queryKeys.taxonomy.TAXONOMY_ITEM,
        taxonomyAlias,
      ]);
      if (prevTaxonomyData) {
        queryClient.setQueryData(
          [...queryKeys.taxonomy.TAXONOMY_ITEM, taxonomyAlias],
          {
            ...prevTaxonomyData,
            prediction_state: payload.prediction_state,
            progress_rate: payload.progress_rate,
          },
        );
      }

      const prevTaxonomyList: TaxonomyT[] | undefined =
        queryClient.getQueryData(queryKeys.taxonomy.TAXONOMY_LIST);
      if (prevTaxonomyList) {
        const newList = [...prevTaxonomyList];
        const targetIndex = newList.findIndex(
          (taxonomy) => taxonomy.alias === taxonomyAlias,
        );
        if (targetIndex === -1) return;

        const newTaxonomy = {
          ...newList[targetIndex],
          prediction_state: payload.prediction_state,
          progress_rate: payload.progress_rate,
        };

        newList.splice(targetIndex, 1, newTaxonomy);
        queryClient.setQueryData(queryKeys.taxonomy.TAXONOMY_LIST, newList);
      }
    },
    [queryClient],
  );

  const updateTopicPredictionState = useCallback(
    (topicAlias: string, payload: PredictionProgressPayload) => {
      setTaxonomyStoreState((prev) => {
        const topicInfo = taxonomyUtils.getTopicWithoutTaxonomy(
          prev.topicTreeMap,
          topicAlias,
        );
        if (!topicInfo) return prev;
        const newTopic: Topic2TreeItemT<TOPIC_2_TYPE> = {
          ...topicInfo,
          prediction_state: payload.prediction_state,
          progress_rate: payload.progress_rate,
        };
        const prevTreeMap = prev.topicTreeMap[topicInfo.taxonomyAlias];
        if (!prevTreeMap) return prev;

        const newTreeMap: Record<string, TopicTreeT> = {
          ...prev.topicTreeMap,
          [topicInfo.taxonomyAlias]: {
            ...prevTreeMap,
            topicMap: {
              ...prevTreeMap.topicMap,
              [topicAlias]: newTopic,
            },
          },
        };
        return {
          ...prev,
          topicTreeMap: newTreeMap,
        };
      });
    },
    [setTaxonomyStoreState],
  );

  return {
    updateTaxonomyPredictionState,
    updateTopicPredictionState,
  };
};

export { usePredictionStateUpdate };

export default useTaxonomyStore;
